import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Helmet } from 'react-helmet';

import { Chess, useChess } from 'api/chess';
import { Box, Button, CustomBox, GridChild, GridContainer, Title } from 'common';
import { ChessCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext';

import { ChessImagesCarousel } from './ChessImagesCarousel';

export const SingleChessPage = () => {
    const { id } = useParams();
    const { onGetOneChess, onGetAllChess } = useChess();

    const [chessDetails, setChessDetails] = useState<Chess | null>(null);
    const [latestChess, setLatestChess] = useState<Chess[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [loadingLatestChess, setLoadingLatestChess] = useState(true);
    const [error, setError] = useState<string>('');

    const { language } = useLanguage();

    useEffect(() => {
        const fetchChessDetails = async () => {
            try {
                const result = await onGetOneChess(Number(id));
                if (result.success) {
                    setChessDetails(result.data);
                } else {
                    setError('Не удалось загрузить детали шахматного клуба.');
                }
            } catch (error) {
                setError('Произошла ошибка при запросе деталей шахматного клуба.');
            } finally {
                setLoadingDetails(false);
            }
        };
        const fetchLatestChess = async () => {
            try {
                const result = await onGetAllChess();
                if (result.success) {
                    const sortedChess = result.data.sort((a: Chess, b: Chess) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                    setLatestChess(sortedChess);
                } else {
                    setError('Произошла ошибка при загрузке списка шахматного клуба.');
                }
            } catch (error) {
                setError('Произошла ошибка при запросе списка шахматного клуба.');
            } finally {
                setLoadingLatestChess(false);
            }
        };
        fetchChessDetails();
        fetchLatestChess();
    }, [id, onGetOneChess, onGetAllChess]);


    if (loadingDetails && loadingLatestChess)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );

    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    {chessDetails && (
                        <CustomBox title={chessDetails[`title_${language}`]}>
                            <Helmet>
                                <title>{chessDetails[`title_${language}`]}</title>
                                <meta name="description" content="Информация о библиотеке AlmaU Open Library" />
                            </Helmet>
                            {chessDetails.images && chessDetails.images.length > 0 && chessDetails.images[0] ? (
                                <ChessImagesCarousel chessItem={chessDetails} />
                            ) : (<ImageNotSupportedIcon
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '5rem'
                                }}
                            />)}
                            <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                {chessDetails[`title_${language}`]}
                            </Title>
                            <Title gutterBottom variant="body2" color="text.secondary">
                                <div dangerouslySetInnerHTML={{
                                    __html: chessDetails[`description_${language}`]
                                }} />
                            </Title>
                            <Title variant="body2" color="text.primary">
                                {dayjs(chessDetails.created_at).format('DD.MM.YYYY')}
                            </Title>
                        </CustomBox>
                    )}
                </GridChild>

                <GridChild md={4}>
                    {!loadingLatestChess && (
                        <CustomBox title="Последние шахматного клуба">
                            {latestChess.slice(0, 3).map((chessItem) => (
                                <ChessCard key={chessItem.id} chessItem={chessItem} />
                            ))}

                            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'start' }}>
                                <Button variant="contained" color="error" href="/chess-club">
                                    Ко всем шахматным клубам
                                </Button>
                            </Box>
                        </CustomBox>
                    )}
                </GridChild>
            </GridContainer>
        </Box>
    );
};
